/**
 * Blog Category Entity Schemas
 *
 * A managed, platform-global topic label on a marketing blog post (SCH-2108).
 * A post carries at most one category, or none. The slug names the public page
 * `/blog/category/<slug>` and is fixed at creation; the name and the sort
 * position are editable by a super-admin.
 *
 * The slug is a plain string here, as on `BlogPostBaseSchema`: these are the
 * shapes the server returns. Request-side slug validation reuses
 * `BlogPostSlugSchema` in `api/blog/request.ts`. Importing it here instead
 * would cycle (`blog-post.ts` → this module → `request.ts` → `blog-post.ts`).
 *
 * See docs/_archive/2026-10-17-plans-sweep/plans/2026-09-21-1718-feat-blog-categories-plan.md.
 */

import { z } from 'zod'

/** A category's display name. Names need not be unique; slugs must. */
export const BlogCategoryNameSchema = z.string().trim().min(1).max(80)

/** The category as a post carries it: enough to render a label that links to the category page. */
export const BlogCategoryRefSchema = z.object({
  publicId: z.string(),
  slug: z.string(),
  name: z.string()
})
export type BlogCategoryRef = z.infer<typeof BlogCategoryRefSchema>

export const BlogCategorySchema = BlogCategoryRefSchema.extend({
  sortOrder: z.number().int()
})
export type BlogCategory = z.infer<typeof BlogCategorySchema>

/** Admin view: every category, with how many posts (draft or published) use it. */
export const BlogCategoryWithPostCountSchema = BlogCategorySchema.extend({
  postCount: z.number().int().min(0)
})
export type BlogCategoryWithPostCount = z.infer<typeof BlogCategoryWithPostCountSchema>
